import { CanAccess, useLogout, useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useWeeklyGoal } from "../myday/useWeeklyGoal";
import { useAgendaUrl } from "../templates/useTemplateFor";
import { fillTemplate } from "../templates/fillTemplate";
import { MsgReviewSection } from "../templates/MsgReviewSection";
import {
  useTranscriptNotice,
  type TranscriptNotice,
} from "../calls/transcriptConsent";
import { KeysSection } from "./KeysSection";
import { FreelancersSection } from "./FreelancersSection";
import { DenueSection } from "../denue/denue";
import { ReviewsSettings } from "../reviews/reviews";
import {
  AiSection,
  CalibrationProposal,
  ResearchEmailSection,
} from "../ai/AiSection";
import { Link } from "react-router";
import {
  Ban,
  FileCheck2,
  ListOrdered,
  LogOut,
  MessageSquareText,
  Monitor,
  Moon,
  Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/components/admin/use-theme";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobilePage } from "../MobilePage";
import { useEffect, useState } from "react";
import { OfferForm, type OfferFormTarget } from "../pipeline/OfferForm";
import { OfferWizard } from "../wizard/OfferWizard";
import { useDuenoStatus, useIsDueno } from "../useIsDueno";
import { OwnerOnlyNotice } from "../OwnerOnly";
import { OfferDot } from "../ui";
import { offerColor, useAllOffers } from "../useOffers";
import {
  rebalance,
  TH_LAB,
  thLevel,
  W_KEYS,
  type Weights,
} from "../thermo/thermo";

const Section = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <Card className={cn("gap-3 p-4", className)}>
    <h2 className="text-base font-extrabold">{title}</h2>
    {children}
  </Card>
);

// Lo que se configura aquí cuando llegue cada bloque

/** "IA y ajustes": una sola pantalla de configuración (PRD v2 · Ajustes). */
export const AjustesPage = () => {
  const { theme, setTheme } = useTheme();
  const logout = useLogout();
  const isMobile = useIsMobile();

  const tools = [
    {
      to: "/templates",
      Icon: MessageSquareText,
      title: "Plantillas",
      desc: "Textos de WhatsApp, correo y guiones por producto, con variables como {{empresa}}.",
    },
    {
      to: "/sequences",
      Icon: ListOrdered,
      title: "Cadencias",
      desc: "Series de pasos (llamar, WhatsApp, correo) en días fijos; crean las tareas de Mi día.",
    },
    {
      to: "/textos",
      Icon: FileCheck2,
      title: "Textos por validar",
      desc: "Promesas de guiones y propuestas y las plantillas: apruébalas, edítalas o descártalas. La IA solo usa las aprobadas.",
    },
    {
      to: "/do_not_contact",
      Icon: Ban,
      title: "No contactar",
      desc: "Teléfonos y correos bloqueados (bajas, REPEP, solicitudes ARCO).",
    },
  ];

  const dueno = useDuenoStatus();
  return (
    <MobilePage title="IA y ajustes">
      <div className="flex max-w-4xl flex-col gap-4">
        <div className="max-md:hidden">
          <h1 className="text-2xl font-extrabold tracking-tight">
            IA y ajustes
          </h1>
          <p className="text-sm text-muted-foreground">
            Motor de IA, textos, ventas, termómetro, agenda, llaves y
            apariencia, reseñas de Google y sincronización con el DENUE.
          </p>
        </div>

        {/* Owner-only settings (keys, AI, thermometer, reviews, DENUE): the freelancer role cannot read them */}
        {dueno === "no" && (
          <OwnerOnlyNotice message="Los ajustes del CRM (IA, llaves, termómetro, reseñas y DENUE) los administra el dueño. Aquí puedes cambiar la apariencia y tu cuenta." />
        )}
        {dueno === "dueno" && (
          <>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {tools.map(({ to, Icon, title, desc }) => (
                <Card key={to} className="gap-2 p-4">
                  <div className="flex items-center gap-2 font-extrabold">
                    <Icon className="size-5 text-primary" />
                    {title}
                  </div>
                  <p className="flex-1 text-sm text-muted-foreground">{desc}</p>
                  <Button asChild variant="outline" className="self-start">
                    <Link to={to}>Abrir {title.toLowerCase()}</Link>
                  </Button>
                </Card>
              ))}
            </div>

            <AiSection />

            <ResearchEmailSection />

            <GoalSection />

            <AgendaSection />

            <TranscriptNoticeSection />

            <ThermoSection />

            <OffersSection />

            <FreelancersSection />

            <MsgReviewSection />

            <Section title="Reseñas de Google">
              <ReviewsSettings />
            </Section>

            <KeysSection />
          </>
        )}

        <Section title="Apariencia">
          <div className="inline-flex self-start overflow-hidden rounded-lg border">
            {(
              [
                ["light", "Claro", Sun],
                ["dark", "Oscuro", Moon],
                ["system", "Automático", Monitor],
              ] as const
            ).map(([id, label, Icon]) => (
              <button
                key={id}
                onClick={() => setTheme(id)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-sm font-semibold",
                  theme === id
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground",
                )}
              >
                <Icon className="size-4" />
                {label}
              </button>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Automático sigue el modo de tu computadora o celular. Se recuerda en
            cada aparato.
          </p>
        </Section>

        {dueno === "dueno" && (
          <Section title="Sincronización con el DENUE (INEGI)">
            <DenueSection />
          </Section>
        )}

        <Section title="Cuenta">
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to={isMobile ? "/settings" : "/profile"}>Mi perfil</Link>
            </Button>
            {!isMobile && (
              <CanAccess resource="sales" action="list">
                <Button asChild variant="outline">
                  <Link to="/sales">Usuarios</Link>
                </Button>
              </CanAccess>
            )}
            {!isMobile && (
              <CanAccess resource="configuration" action="edit">
                <Button asChild variant="outline">
                  <Link to="/settings">Configuración del CRM</Link>
                </Button>
              </CanAccess>
            )}
            <Button variant="outline" onClick={() => logout()}>
              <LogOut />
              Cerrar sesión
            </Button>
          </div>
        </Section>
      </div>
    </MobilePage>
  );
};

/** Tableros de productos: los activos y los archivados (para volver a mostrarlos). */
const OffersSection = () => {
  const { data: offers } = useAllOffers();
  const [target, setTarget] = useState<OfferFormTarget | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const isDueno = useIsDueno();
  return (
    <Section title="Tableros de productos">
      <ul className="divide-y">
        {offers?.map((o) => (
          <li key={o.id} className="flex items-center gap-3 py-2">
            <OfferDot color={offerColor(o)} />
            <span className="font-mono text-xs text-muted-foreground">
              {o.key}
            </span>
            <span className="flex-1 font-semibold">
              {o.name}
              {!o.active && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  archivado
                </span>
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTarget({ mode: "edit", offer: o })}
            >
              Editar
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          className="self-start"
          onClick={() => setTarget({ mode: "create" })}
        >
          + Nuevo producto o servicio
        </Button>
        {/* Publishing runs apply_offer_fit_rule, which only the owner may run */}
        {isDueno && (
          <Button
            variant="outline"
            className="self-start"
            onClick={() => setWizardOpen(true)}
          >
            Asistente con cliente ideal (beta)
          </Button>
        )}
      </div>
      <OfferForm target={target} onClose={() => setTarget(null)} />
      {isDueno && (
        <OfferWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      )}
    </Section>
  );
};

/** Meta semanal de demos (PRD C.5): se guarda en crm_settings (solo admin). */
const GoalSection = () => {
  const { data } = useWeeklyGoal();
  const [goal, setGoal] = useState<string>("");
  const notify = useNotify();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (data) setGoal(String(data.goal));
  }, [data]);
  const save = async () => {
    const n = Math.max(1, Math.round(Number(goal)) || 1);
    const { data: rows, error } = await getSupabaseClient()
      .from("crm_settings")
      .update({ weekly_goal: n, updated_at: new Date().toISOString() })
      .eq("id", 1)
      .select("id");
    if (error || !rows?.length)
      return notify("Solo un administrador puede cambiar la meta", {
        type: "error",
      });
    queryClient.invalidateQueries({ queryKey: ["weekly_goal"] });
    notify(`Meta: ${n} demos por semana`, { type: "success" });
  };
  return (
    <Section title="Meta semanal">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Input
          type="number"
          min={1}
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="w-20"
          aria-label="Demos por semana"
        />
        demos agendadas por semana
        <Button variant="outline" size="sm" onClick={save}>
          Guardar
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Se ve en Mi día con tu racha: semanas seguidas cumpliendo la meta,
        contando las llamadas registradas como "Demo agendada". Esta semana
        llevas {data?.thisWeek ?? 0}; racha {data?.streak ?? 0}.
      </p>
    </Section>
  );
};

type ThermoSettings = {
  thermo_weights: Weights;
  thermo_decay: { start: number; per: number; max: number };
  alert_idle: boolean;
  alert_idle_days: number;
  alert_drop: number;
  alert_hot: number;
  calibration: boolean;
  thermo_job: {
    status: "pendiente" | "corriendo" | "lista";
    done?: number;
    total?: number;
    secs?: number;
    finished_at?: string;
  } | null;
};
const THERMO_COLS =
  "thermo_weights, thermo_decay, alert_idle, alert_idle_days, alert_drop, alert_hot, calibration, thermo_job";
const CALIBRATION_AT = 30;

/** Termómetro (PRD E.1, E.5, E.6): pesos, enfriamiento, alertas y calibración. Solo admin guarda. */
const ThermoSection = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [cfg, setCfg] = useState<ThermoSettings | null>(null);
  const running = (s?: ThermoSettings | null) =>
    s?.thermo_job?.status === "pendiente" ||
    s?.thermo_job?.status === "corriendo";
  const { data } = useQuery({
    queryKey: ["crm_settings", "thermo"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("crm_settings")
        .select(THERMO_COLS)
        .eq("id", 1)
        .single();
      if (error) throw error;
      return data as ThermoSettings;
    },
    // mientras se recalcula el termómetro base, mostrar el avance
    refetchInterval: (q) => (running(q.state.data) ? 3000 : false),
  });
  const { data: closed } = useQuery({
    queryKey: ["thermo_calibration"],
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("thermo_calibration")
        .select("closed")
        .single();
      return (data?.closed as number) ?? 0;
    },
  });
  useEffect(() => {
    if (data && !cfg) setCfg(data);
  }, [data, cfg]);
  const w = cfg?.thermo_weights;
  const [debounced, setDebounced] = useState(w);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(w), 250);
    return () => clearTimeout(t);
  }, [w]);
  const { data: example } = useQuery({
    queryKey: ["thermo_preview", debounced],
    enabled: !!debounced,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("thermo_preview", {
        p_weights: debounced,
      });
      return data?.[0] as { name: string; value: number } | undefined;
    },
  });
  if (!cfg || !w) return null;

  const set = (patch: Partial<ThermoSettings>) => setCfg({ ...cfg, ...patch });
  const num = (v: string, min = 0) => Math.max(min, Math.round(Number(v)) || 0);
  const save = async () => {
    const { thermo_job: _job, ...values } = cfg;
    const { data: rows, error } = await getSupabaseClient()
      .from("crm_settings")
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq("id", 1)
      .select(THERMO_COLS);
    if (error || !rows?.length)
      return notify("Solo un administrador puede cambiar el termómetro", {
        type: "error",
      });
    queryClient.setQueryData(["crm_settings", "thermo"], rows[0]);
    ["deal_board", "thermo_alerts", "my_day", "directory_thermo"].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }),
    );
    notify(
      running(rows[0] as ThermoSettings)
        ? "Guardado. El termómetro de las 2.5 M empresas se recalcula en segundo plano."
        : "Guardado",
      { type: "success" },
    );
  };
  const job = data?.thermo_job;
  const pct = job?.total ? Math.round(((job.done ?? 0) / job.total) * 100) : 0;
  const cal = Math.min(closed ?? 0, CALIBRATION_AT);
  const inCls = "w-16 h-8";

  return (
    <Section title="Termómetro de éxito">
      <div className="grid gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          <h3 className="font-bold">Pesos</h3>
          <p className="text-sm text-muted-foreground">
            Mueve cada peso; los demás se reparten para sumar 100.
            {example && (
              <>
                {" "}
                Ejemplo en vivo: {example.name} en Storcky ={" "}
                <b>{example.value}</b> ({thLevel(example.value)[1]}).
              </>
            )}
          </p>
          {W_KEYS.map((k) => (
            <label
              key={k}
              className="grid grid-cols-[1fr_auto] items-center gap-x-3 text-sm"
            >
              <span>{TH_LAB[k]}</span>
              <b className="w-10 text-right tabular-nums">{w[k]}%</b>
              <input
                type="range"
                min={0}
                max={100}
                value={w[k]}
                onChange={(e) =>
                  set({
                    thermo_weights: rebalance(w, k, Number(e.target.value)),
                  })
                }
                aria-label={TH_LAB[k]}
                className="col-span-2 accent-[var(--primary)]"
              />
            </label>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() =>
              set({
                thermo_weights: {
                  fit: 30,
                  cont: 20,
                  size: 15,
                  sig: 15,
                  int: 20,
                },
              })
            }
          >
            Restablecer 30/20/15/15/20
          </Button>
          {job && (
            <div className="flex flex-col gap-1 text-sm">
              {running(data) ? (
                <>
                  <span>
                    {job.status === "pendiente"
                      ? "Recálculo en cola: empieza en menos de un minuto."
                      : `Recalculando el termómetro base: ${(job.done ?? 0).toLocaleString("es-MX")} de ${(job.total ?? 0).toLocaleString("es-MX")} empresas (${pct} %).`}
                  </span>
                  <span className="h-2 overflow-hidden rounded-full bg-secondary">
                    <i
                      className="block h-full rounded-full bg-primary transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Último recálculo del directorio:{" "}
                  {job.finished_at
                    ? new Date(job.finished_at).toLocaleString("es-MX")
                    : "—"}
                  {job.secs != null && ` (${Math.round(job.secs / 60)} min)`}.
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 text-sm">
          <h3 className="font-bold">Enfriamiento por inactividad</h3>
          <p className="flex flex-wrap items-center gap-1.5">
            Empieza después de
            <Input
              type="number"
              min={1}
              className={inCls}
              value={cfg.thermo_decay.start}
              onChange={(e) =>
                set({
                  thermo_decay: {
                    ...cfg.thermo_decay,
                    start: num(e.target.value, 1),
                  },
                })
              }
              aria-label="Días sin contacto"
            />
            días sin contacto, pierde
            <Input
              type="number"
              min={0}
              className={inCls}
              value={cfg.thermo_decay.per}
              onChange={(e) =>
                set({
                  thermo_decay: {
                    ...cfg.thermo_decay,
                    per: num(e.target.value),
                  },
                })
              }
              aria-label="Puntos por día"
            />
            punto(s) por día, hasta
            <Input
              type="number"
              min={0}
              className={inCls}
              value={cfg.thermo_decay.max}
              onChange={(e) =>
                set({
                  thermo_decay: {
                    ...cfg.thermo_decay,
                    max: num(e.target.value),
                  },
                })
              }
              aria-label="Máximo de puntos"
            />
            puntos.
          </p>

          <h3 className="font-bold">Alertas</h3>
          <label className="flex flex-wrap items-center gap-1.5">
            <input
              type="checkbox"
              className="size-4 accent-[var(--primary)]"
              checked={cfg.alert_idle}
              onChange={(e) => set({ alert_idle: e.target.checked })}
            />
            Avisarme si una tibia o más caliente pasa de
            <Input
              type="number"
              min={1}
              className={inCls}
              value={cfg.alert_idle_days}
              onChange={(e) => set({ alert_idle_days: num(e.target.value, 1) })}
              aria-label="Días sin contacto para avisar"
            />
            días sin contacto
          </label>
          <p className="flex flex-wrap items-center gap-1.5">
            Avisarme si una oportunidad baja
            <Input
              type="number"
              min={1}
              className={inCls}
              value={cfg.alert_drop}
              onChange={(e) => set({ alert_drop: num(e.target.value, 1) })}
              aria-label="Puntos que baja"
            />
            puntos o más en 2 semanas
          </p>
          <p className="flex flex-wrap items-center gap-1.5">
            Empresa muy caliente fuera del CRM: termómetro de
            <Input
              type="number"
              min={1}
              max={100}
              className={inCls}
              value={cfg.alert_hot}
              onChange={(e) => set({ alert_hot: num(e.target.value, 1) })}
              aria-label="Termómetro mínimo"
            />
            o más (las 10 más calientes de tus productos de prioridad 1, primero
            Querétaro).
          </p>
          <p className="text-muted-foreground">
            “Se calentó” avisa cuando sube 6 puntos o más en 2 semanas y queda
            en 55 o más.
          </p>

          <h3 className="font-bold">Calibración con tus resultados</h3>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="size-4 accent-[var(--primary)]"
              checked={cfg.calibration}
              onChange={(e) => set({ calibration: e.target.checked })}
            />
            Ajustar pesos y probabilidades con lo que realmente cierras
          </label>
          <div className="flex items-center gap-2">
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
              <i
                className="block h-full rounded-full bg-primary"
                style={{ width: `${(cal / CALIBRATION_AT) * 100}%` }}
              />
            </span>
            <span className="text-muted-foreground">
              {closed ?? 0} de {CALIBRATION_AT} cerradas
            </span>
          </div>
          <p className="text-muted-foreground">
            Al llegar a 30 oportunidades ganadas o perdidas, la IA compara qué
            tenían las que cerraron y te propone pesos nuevos. Tú apruebas el
            cambio.
          </p>
          <CalibrationProposal />
        </div>
      </div>
      <Button className="self-start" onClick={save}>
        Guardar termómetro
      </Button>
    </Section>
  );
};

/** Liga de agenda (PRD F.4): va en {{agenda}}, en el cierre del guion y en la sesión. */
const AgendaSection = () => {
  const current = useAgendaUrl();
  const [url, setUrl] = useState("");
  const notify = useNotify();
  const queryClient = useQueryClient();
  useEffect(() => setUrl(current ?? ""), [current]);
  const save = async () => {
    const v = url.trim();
    if (v && !/^https:\/\/\S+\.\S+/.test(v))
      return notify("La liga debe empezar con https://", { type: "error" });
    const { data: rows, error } = await getSupabaseClient()
      .from("crm_settings")
      .update({ agenda_url: v || null, updated_at: new Date().toISOString() })
      .eq("id", 1)
      .select("id");
    if (error || !rows?.length)
      return notify("Solo un administrador puede cambiar la agenda", {
        type: "error",
      });
    queryClient.invalidateQueries({ queryKey: ["crm_settings"] });
    notify(v ? "Liga de agenda guardada" : "Liga de agenda quitada", {
      type: "success",
    });
  };
  return (
    <Section title="Agenda (Cal.com)">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="url"
          placeholder="https://cal.com/tu-usuario/demo-15-min"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="max-w-md"
          aria-label="Liga de agenda"
        />
        <Button variant="outline" size="sm" onClick={save}>
          Guardar
        </Button>
        {current && (
          <Button asChild variant="ghost" size="sm">
            <a href={current} target="_blank" rel="noreferrer">
              Probar liga
            </a>
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Se inserta en las plantillas con {"{{agenda}}"}, en el cierre del guion
        y en la sesión de llamadas. Cuando alguien agenda, Cal.com avisa al CRM:
        registra “Demo agendada”, mueve la oportunidad a Demo y crea la tarea
        del día de la demo.
      </p>
      <CanAccess resource="sales" action="list">
        <WebhookCode />
      </CanAccess>
    </Section>
  );
};

/** Aviso de transcripción (Bloque C2, paso 2): plantilla, firma, liga y retención. */
const TranscriptNoticeSection = () => {
  const { data } = useTranscriptNotice();
  const [cfg, setCfg] = useState<TranscriptNotice | null>(null);
  const notify = useNotify();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (data && !cfg) setCfg(data);
  }, [data, cfg]);
  if (!cfg) return null;

  const set = (patch: Partial<TranscriptNotice>) =>
    setCfg({ ...cfg, ...patch });
  const save = async () => {
    const { data: rows, error } = await getSupabaseClient()
      .from("crm_settings")
      .update({ transcript_notice: cfg, updated_at: new Date().toISOString() })
      .eq("id", 1)
      .select("transcript_notice");
    if (error || !rows?.length)
      return notify(
        "Solo un administrador puede cambiar el aviso de transcripción",
        { type: "error" },
      );
    const saved = rows[0].transcript_notice as TranscriptNotice;
    setCfg(saved);
    queryClient.setQueryData(["crm_settings", "transcript_notice"], saved);
    notify("Aviso de transcripción guardado", { type: "success" });
  };
  const preview = fillTemplate(cfg.texto, {
    remitente: "Ian",
    firma: cfg.firma,
    enlace_aviso: cfg.enlace_aviso,
  });

  return (
    <Section title="Aviso de transcripción (copiloto de llamadas)">
      <p className="text-sm text-muted-foreground">
        Se muestra en la sesión de llamadas, antes de transcribir, con{" "}
        {"{{remitente}}"}, {"{{firma}}"} y {"{{enlace_aviso}}"} ya sustituidos.
        Versión actual del texto: {cfg.version}.
      </p>
      <Textarea
        rows={4}
        value={cfg.texto}
        onChange={(e) => set({ texto: e.target.value })}
        aria-label="Texto del aviso de transcripción"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          Firma (cómo te presentas)
          <Input
            value={cfg.firma}
            onChange={(e) => set({ firma: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Enlace al aviso de privacidad
          <Input
            type="url"
            placeholder="https://…"
            value={cfg.enlace_aviso ?? ""}
            onChange={(e) => set({ enlace_aviso: e.target.value || null })}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Conservar el texto transcrito (días)
          <Input
            type="number"
            min={1}
            className="w-24"
            value={cfg.retencion_dias}
            onChange={(e) =>
              set({
                retencion_dias: Math.max(
                  1,
                  Math.round(Number(e.target.value)) || 1,
                ),
              })
            }
          />
        </label>
      </div>
      <p className="text-sm text-muted-foreground">Vista previa: "{preview}"</p>
      <Button className="self-start" onClick={save}>
        Guardar
      </Button>
    </Section>
  );
};

const WEBHOOK_URL =
  "https://api.raftelcrm.duckdns.org/functions/v1/calcom-webhook";

/** Aviso automático de Cal.com: dirección y código secreto (lo genera el CRM). */
const WebhookCode = () => {
  const notify = useNotify();
  const [code, setCode] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const sb = getSupabaseClient();
  useEffect(() => {
    sb.rpc("calcom_secret_get").then(({ data }) => setCode(data ?? null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const copy = (text: string, what: string) =>
    navigator.clipboard
      .writeText(text)
      .then(() => notify(`${what} copiado`, { type: "info" }))
      .catch(() =>
        notify("No se pudo copiar: selecciónalo a mano", { type: "warning" }),
      );
  const regenerate = async () => {
    if (
      code &&
      !window.confirm(
        "Si generas un código nuevo, los avisos de Cal.com dejarán de llegar hasta que pegues el nuevo en Cal.com. ¿Continuar?",
      )
    )
      return;
    setBusy(true);
    const { data, error } = await sb.rpc("calcom_secret_regenerate");
    setBusy(false);
    if (error) return notify(error.message, { type: "error" });
    setCode(data);
    setShow(true);
    notify("Código nuevo listo: cópialo y pégalo en Cal.com", {
      type: "success",
    });
  };
  const box =
    "min-w-0 flex-1 truncate rounded-md border bg-secondary px-2.5 py-1.5 font-mono text-sm";
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <b className="text-sm">Aviso automático (webhook) para Cal.com</b>
      <span className="text-xs text-muted-foreground">
        Dirección (Subscriber URL)
      </span>
      <div className="flex items-center gap-2">
        <span className={box}>{WEBHOOK_URL}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => copy(WEBHOOK_URL, "Dirección")}
        >
          Copiar
        </Button>
      </div>
      <span className="text-xs text-muted-foreground">
        Código secreto (Secret)
      </span>
      {code ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className={box} aria-label="Código secreto">
            {show ? code : "•".repeat(24)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => copy(code, "Código")}
          >
            Copiar
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShow(!show)}>
            {show ? "Ocultar" : "Mostrar"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={regenerate}
          >
            Regenerar
          </Button>
        </div>
      ) : (
        <Button
          className="self-start"
          size="sm"
          disabled={busy}
          onClick={regenerate}
        >
          Generar código
        </Button>
      )}
      <p className="text-xs text-muted-foreground">
        El CRM solo acepta avisos firmados con este código. Se guarda cifrado en
        el servidor.
      </p>
    </div>
  );
};
